/**
 * FitForUseIndicator - FDA Fit-for-Use Determination Component
 * ============================================================================
 * DC-932: Standalone reusable component for displaying FDA AI Credibility
 * fit-for-use determination with relevance/reliability breakdown.
 *
 * UX Design Philosophy:
 * - Lead with the answer (Fit/Not Fit prominently displayed)
 * - Support with evidence (dimension scores as secondary)
 * - Actionable CTAs when not fit (View Gaps, Export Report)
 * - Progressive disclosure (sub-dimensions on demand)
 *
 * Pattern: Follows FDACredibilityCard.tsx (DC-930)
 *
 * @version 1.0.0
 * @created 2026-01-10
 * ============================================================================
 */

import React, { useState, useCallback } from 'react';
import './fit-for-use-indicator.css';

// ============================================================================
// Types
// ============================================================================

export type FDACredibilityLevel = 'high' | 'adequate' | 'low' | 'insufficient';

export interface SubDimensionScore {
  score: number;
  passed: boolean;
}

export interface FitForUseIndicatorProps {
  /** Relevance dimension score (0-100); undefined = not assessed (no fabricated fallback) */
  relevanceScore?: number;
  /** Reliability dimension score (0-100); undefined = not assessed (no fabricated fallback) */
  reliabilityScore?: number;
  /** Overall credibility score (0-100) */
  overallScore: number;
  /** Relevance sub-dimension scores */
  relevanceSubScores?: {
    contextOfUse: SubDimensionScore;
    representativeness: SubDimensionScore;
    sampleSize: SubDimensionScore;
  };
  /** Reliability sub-dimension scores */
  reliabilitySubScores?: {
    accuracy: SubDimensionScore;
    completeness: SubDimensionScore;
    traceability: SubDimensionScore;
  };
  /** Final fit-for-use determination */
  isFitForUse: boolean;
  /** Credibility level */
  credibilityLevel: FDACredibilityLevel;
  /** Number of gaps blocking fit-for-use (for messaging) */
  gapsCount?: number;
  /** Required evidence level based on model risk */
  requiredEvidenceLevel?: 'basic' | 'standard' | 'comprehensive';
  /** Callback when "View Gaps" button is clicked */
  onViewGaps?: () => void;
  /** Callback when "Export Report" button is clicked */
  onExportReport?: () => void;
  /** Compact mode for smaller layouts */
  compact?: boolean;
  /** Show sub-dimension breakdown */
  showSubDimensions?: boolean;
  /** Custom className */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const CREDIBILITY_LEVEL_LABELS: Record<FDACredibilityLevel, string> = {
  high: 'High Credibility',
  adequate: 'Adequate Credibility',
  low: 'Low Credibility',
  insufficient: 'Insufficient Credibility',
};

const EVIDENCE_LEVEL_LABELS: Record<string, string> = {
  basic: 'Basic',
  standard: 'Standard',
  comprehensive: 'Comprehensive',
};

/** FDA-grounded definitions (Fit-for-Use Framework) shown under each dimension. */
const DIMENSION_DEFINITIONS: Record<string, string> = {
  Relevance:
    'Are the data adequate to answer the question for this context of use — the right data elements, a representative population, and sufficient quantity? (FDA Fit-for-Use: Relevance — Context of Use + Representativeness + Sample Size)',
  Reliability:
    'Are the data accurate, complete, and traceable — well-collected and conforming to data-quality and integrity standards? (FDA Fit-for-Use: Reliability — Accuracy + Completeness + Traceability)',
};

/** A dimension passes at or above this score (the lens threshold). */
const PASS_THRESHOLD = 70;

// ============================================================================
// Helper Components
// ============================================================================

interface DimensionCardProps {
  title: string;
  score?: number;
  passed: boolean;
  subScores?: Array<{ label: string; score: number; passed: boolean }>;
  expanded: boolean;
  onToggle: () => void;
  compact?: boolean;
}

const DimensionCard: React.FC<DimensionCardProps> = ({
  title,
  score,
  passed,
  subScores,
  expanded,
  onToggle,
  compact,
}) => {
  const scoreColor = passed ? '#16A34A' : '#DC2626';
  const hasSubScores = subScores && subScores.length > 0;

  return (
    <div className={`ffu-dimension-card ${compact ? 'ffu-dimension-card--compact' : ''}`}>
      <button
        className="ffu-dimension-card__header"
        onClick={onToggle}
        aria-expanded={expanded}
        disabled={!hasSubScores}
      >
        <div className="ffu-dimension-card__title-row">
          <span className="ffu-dimension-card__title">{title}</span>
          {hasSubScores && (
            <svg
              className={`ffu-dimension-card__chevron ${expanded ? 'ffu-dimension-card__chevron--expanded' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
        {DIMENSION_DEFINITIONS[title] && (
          <p className="ffu-dimension-card__def">{DIMENSION_DEFINITIONS[title]}</p>
        )}
        <div className="ffu-dimension-card__score-row">
          {score === undefined ? (
            // No fabricated fallback — absent evidence renders as "Not assessed".
            <span className="ffu-dimension-card__score ffu-dimension-card__score--na">Not assessed</span>
          ) : (
            <>
              <span
                className="ffu-dimension-card__score"
                style={{ color: scoreColor }}
              >
                {Math.round(score)}%
              </span>
              <span
                className={`ffu-dimension-card__status ffu-dimension-card__status--${passed ? 'pass' : 'fail'}`}
              >
                {passed ? '✓ Pass' : '✗ Fail'}
              </span>
              <span className="ffu-dimension-card__threshold">Pass &ge; {PASS_THRESHOLD}%</span>
            </>
          )}
        </div>
      </button>

      {expanded && hasSubScores && (
        <div className="ffu-dimension-card__details">
          {subScores.map((sub) => (
            <div key={sub.label} className="ffu-dimension-card__sub-item">
              <span className="ffu-dimension-card__sub-label">{sub.label}</span>
              <span className="ffu-dimension-card__sub-score">{Math.round(sub.score)}%</span>
              <span
                className={`ffu-dimension-card__sub-status ffu-dimension-card__sub-status--${sub.passed ? 'pass' : 'fail'}`}
              >
                {sub.passed ? '✓' : '⚠'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const FitForUseIndicator: React.FC<FitForUseIndicatorProps> = ({
  relevanceScore,
  reliabilityScore,
  overallScore,
  relevanceSubScores,
  reliabilitySubScores,
  isFitForUse,
  credibilityLevel,
  gapsCount = 0,
  requiredEvidenceLevel,
  onViewGaps,
  onExportReport,
  compact = false,
  showSubDimensions = true,
  className = '',
}) => {
  const [relevanceExpanded, setRelevanceExpanded] = useState(false);
  const [reliabilityExpanded, setReliabilityExpanded] = useState(false);

  const relevancePassed = relevanceScore !== undefined && relevanceScore >= 70;
  const reliabilityPassed = reliabilityScore !== undefined && reliabilityScore >= 70;

  const handleRelevanceToggle = useCallback(() => {
    setRelevanceExpanded((prev) => !prev);
  }, []);

  const handleReliabilityToggle = useCallback(() => {
    setReliabilityExpanded((prev) => !prev);
  }, []);

  // Build sub-scores arrays for dimension cards
  const relevanceSubs = relevanceSubScores
    ? [
        { label: 'Context of Use', ...relevanceSubScores.contextOfUse },
        { label: 'Representativeness', ...relevanceSubScores.representativeness },
        { label: 'Sample Size', ...relevanceSubScores.sampleSize },
      ]
    : undefined;

  const reliabilitySubs = reliabilitySubScores
    ? [
        { label: 'Accuracy', ...reliabilitySubScores.accuracy },
        { label: 'Completeness', ...reliabilitySubScores.completeness },
        { label: 'Traceability', ...reliabilitySubScores.traceability },
      ]
    : undefined;

  return (
    <div
      className={`ffu-indicator ${compact ? 'ffu-indicator--compact' : ''} ${className}`}
      role="region"
      aria-label="FDA Fit-for-Use Determination"
    >
      {/* Primary Determination Banner */}
      <div
        className={`ffu-indicator__banner ffu-indicator__banner--${isFitForUse ? 'fit' : 'not-fit'}`}
      >
        <div className="ffu-indicator__banner-icon">
          {isFitForUse ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
        </div>
        <div className="ffu-indicator__banner-content">
          <h3 className="ffu-indicator__banner-title">
            {isFitForUse ? 'FIT FOR USE' : 'NOT FIT FOR USE'}
          </h3>
          <p className="ffu-indicator__banner-subtitle">
            {isFitForUse
              ? `Fit for this context of use — ${CREDIBILITY_LEVEL_LABELS[credibilityLevel]} (${Math.round(overallScore)}%)`
              : gapsCount > 0
              ? `For this context of use — ${gapsCount} gap${gapsCount === 1 ? '' : 's'} must be resolved first`
              : 'Not adequate for this context of use'}
          </p>
        </div>

        {/* CTAs for Not Fit state */}
        {!isFitForUse && (onViewGaps || onExportReport) && (
          <div className="ffu-indicator__banner-actions">
            {onViewGaps && (
              <button
                className="ffu-indicator__action-btn ffu-indicator__action-btn--primary"
                onClick={onViewGaps}
              >
                View Gaps
              </button>
            )}
            {onExportReport && (
              <button
                className="ffu-indicator__action-btn ffu-indicator__action-btn--secondary"
                onClick={onExportReport}
              >
                Export Report
              </button>
            )}
          </div>
        )}
      </div>

      {/* Dimension Cards */}
      {showSubDimensions && (
        <div className="ffu-indicator__dimensions">
          <DimensionCard
            title="Relevance"
            score={relevanceScore}
            passed={relevancePassed}
            subScores={relevanceSubs}
            expanded={relevanceExpanded}
            onToggle={handleRelevanceToggle}
            compact={compact}
          />
          <DimensionCard
            title="Reliability"
            score={reliabilityScore}
            passed={reliabilityPassed}
            subScores={reliabilitySubs}
            expanded={reliabilityExpanded}
            onToggle={handleReliabilityToggle}
            compact={compact}
          />
        </div>
      )}

      {/* Evidence Level Footer */}
      {requiredEvidenceLevel && (
        <div className="ffu-indicator__footer">
          <span className="ffu-indicator__evidence-label">Evidence Level Required:</span>
          <span className="ffu-indicator__evidence-value">
            {EVIDENCE_LEVEL_LABELS[requiredEvidenceLevel] || requiredEvidenceLevel}
          </span>
          <span className="ffu-indicator__fda-ref">
            Set by model risk — aligned to FDA&rsquo;s risk-based credibility framework
          </span>
        </div>
      )}
    </div>
  );
};

export default FitForUseIndicator;
